// Disable specific strict options for the whole file:
// @ts-strictNullChecks: false
// @ts-noImplicitAny: false
import { useBldgStore } from "./bldgStore";
import {
  SiteRoleDTO,
  UserClient,
  LoginRequest,
  LoginPersonDTO,
  LoginVm,
  RegisterRequest,
  PermissionType,
  LoginRegistrationDTO,
  ImpersonateRequest,
} from "./generatedFetchClient";
import { defineStore, acceptHMRUpdate } from "pinia";
import * as msal from "@azure/msal-browser";
import { useMetrics } from "../helpers/metrics";

const userClient = new UserClient();
const staffSiteCode = "staff";
const userStorageKey = "_user";
const metrics = useMetrics();

// A store instance to manage user and login state
export const useUserStore = defineStore({
  id: "userStore",
  state: () => {
    return {
      loginError: "",
      name: "",
      roles: [] as SiteRoleDTO[],
      user: null as LoginPersonDTO | null,
      isUserLoading: false,
      isUserLoadedFromServer: false,
      loggedInSiteCode: null as string | null,
      clientId: "",
      loginAuthority: "",
      passwordAuthority: "",
      knownAuthority: "",
      msalInstance: {} as msal.PublicClientApplication,
      isAuthenticated: false,
      msalConfig: {
        auth: {
          clientId: "",
          authority: "https://login.microsoftonline.com/e801a3ad-3690-4aa0-a142-1d77cb360b07",
        },
        cache: {
          cacheLocation: "localStorage",
        },
      },
    };
  },
  actions: {
    startup() {
      // Load user from Local storage if exists
    },
    setAuthToken(token: string) {
      userClient.authToken = token;
    },
    isLoggedInToPortal(siteCode: string): boolean {
      var cachedUser = JSON.parse(localStorage.getItem(siteCode + userStorageKey));
      if (cachedUser) {
        return true;
      } else {
        return false;
      }
    },
    //This only gets the user and attempts to refresh, a failure to refresh does not mean the user is not logged in
    async getUser(
      siteCode: string,
      portalId: number,
      force: boolean = false,
      clientSystemName: string = ""
    ): Promise<LoginPersonDTO | null> {
      clientSystemName = clientSystemName ?? undefined; //Remove null

      if (siteCode?.length > 0 && this.loggedInSiteCode !== siteCode) {
        console.log(`Getting User for site '${siteCode}'. `);
        this.user = null;
        this.loggedInSiteCode = siteCode;
      }

      // Optimistically pull user from localStorage and return immediately
      var cachedUser = JSON.parse(localStorage.getItem(siteCode + userStorageKey) || null);
      if (cachedUser) {
        //console.log("Found cached user ", cachedUser);
        this.user = cachedUser as LoginPersonDTO;
        userClient.authToken = this.user.token || "";
      }

      // Load user from server once per session.
      // To force a reload (like after profile edit), set force==true
      if (this.isLoggedIn && this.user && !force) {
        return this.user;
      }

      // We might get multiple almost-simultaneous calls at startup.
      // Only let the first one kick-off a server call.
      // Only refresh user from server if we have local cached user object
      //  (when client thinks it is logged in)
      // Also, load user if we have token from querystring (SSO login support)
      if (!this.isUserLoading && (this.user !== null || userClient.authToken !== null)) {
        this.isUserLoading = true;
        console.log("User Loading");

        try {
          let userVm: LoginVm | null = null;
          let token = getAuthToken(siteCode);
          if (token) {
            userVm = await userClient.getUserWithToken(portalId, clientSystemName, siteCode);
          } else {
            userVm = await userClient.getUser(portalId, clientSystemName);
          }

          if (userVm && !userVm.error && (userVm.person?.personId || 0) > 0) {
            console.log("Loaded User from server", userVm.person);

            //Legacy support for old VR code.
            // Add a userId value duplicate for personId
            let personObj: any = userVm.person;
            personObj.userId = personObj.personId;
            this.setAuthToken(personObj?.token);

            localStorage[siteCode + userStorageKey] = JSON.stringify(personObj);

            // @ts-ignore - TODO: Fix this null check
            this.user = userVm.person;

            this.isUserLoading = false;
            this.isUserLoadedFromServer = true;
          } else {
            console.warn(`Error loading user: '${userVm?.error}' `, userVm);
          }
          this.isUserLoading = false;
          this.isUserLoadedFromServer = true;

          //identify user in metrics session
          // @ts-ignore - TODO: Fix this null check
          metrics.identify(this.user);
        } catch (apiError: any) {
          this.isUserLoading = false;
          this.isUserLoadedFromServer = true;

          // If we get a legit unauthorized error from server, log user out locally
          if (
            this.loggedInSiteCode &&
            apiError.status == 401 &&
            apiError.response.includes("Unauthorized")
          ) {
            this.user = null;
            localStorage.removeItem(this.loggedInSiteCode + userStorageKey);
          } else {
            console.warn("Error refreshing user", apiError);
          }
        }
      }
      //this.isUserLoadedFromServer = true;
      return this.user;
    },

    async getStaffUser(force: boolean = false): Promise<LoginPersonDTO> {
      var staffUser = await this.getUser(staffSiteCode, 0, force); // @ts-ignore - TODO: Fix this null check
      return staffUser;
    },
    async logoff() {
      this.user = null;

      if (this.loggedInSiteCode) {
        // @ts-ignore - TODO: Fix this null check
        userClient.setAuthToken(null);
        localStorage.removeItem(this.loggedInSiteCode + userStorageKey);
      }

      try {
        let successfulLogout = await userClient.logout();
        if (successfulLogout) {
          metrics.reset();
          console.log("Successfully logged out of server");
        }
      } catch (error: any) {
        console.warn("Error logging out of server", error);
      }
    },
    logStaffUserOff() {
      // @ts-ignore - TODO: Fix this null check
      userClient.setAuthToken(null);
      localStorage.removeItem(this.loggedInSiteCode + "_token"); //remove old legacy storage key
      localStorage.removeItem(this.loggedInSiteCode + userStorageKey);
      this.user = null;
      metrics.reset();
    },
    async requestPasswordReset(username: string, siteCode: string): Promise<string | null> {
      console.log(`Reset password requested for ${username}.`);
      try {
        await userClient.requestPasswordReset(username, siteCode);
      } catch (apiError: any) {
        console.error("Error requesting reset password", apiError);
        return apiError.response ?? apiError.toString();
      }

      return null; //Successful. Return no error message
    },
    async passwordReset(
      username: string,
      code: string,
      password: string,
      portalId?: number
    ): Promise<string | null> {
      console.log(`Reset password requested`);
      try {
        await userClient.passwordReset(username, code, password, portalId ?? 0);
      } catch (apiError: any) {
        console.error("Error resetting password", apiError);
        return apiError.response ?? apiError.toString();
      }

      return null; //Successful. Return no error message
    },
    async loginUser(
      userName: string,
      password: string,
      portalId?: number,
      clientSystemName?: string,
      regionName?: string,
      siteCode?: string
    ): Promise<LoginVm> {
      //console.log(`Logging in user ${userName}`);
      const loginRequest = {
        userName,
        password,
        portalId,
        clientSystemName,
        regionName,
      } as LoginRequest;

      try {
        let user = await userClient.login(loginRequest);
        if (user && user.person && !user.error && !user.upgradePassword) {
          if (user.person.token) {
            this.setAuthToken(user.person.token);
          }

          this.user = user.person;

          let userString = JSON.stringify(user.person);
          if (siteCode) {
            localStorage[siteCode + userStorageKey] = userString; // @ts-ignore - TODO: Fix this null check
          } else if (clientSystemName && user.person?.allRegistrations?.length > 0) {
            for (var i in user.person.allRegistrations) {
              // @ts-ignore - TODO: Fix this null check
              var portalRegistration = user.person.allRegistrations[i] as LoginRegistrationDTO;
              if (portalRegistration?.siteCode) {
                localStorage[portalRegistration?.siteCode + userStorageKey] = userString;
              }
            }
          }

          this.isUserLoadedFromServer = true;
          this.isUserLoading = false;
          console.log(`Successfully logged in as ${userName}`, user);

          await useBldgStore().tryGetBuildingData(useBldgStore().bldgCode, true);

          return user;
        } else if (user && user.upgradePassword) {
          return user;
        } else {
          console.warn("Error logging in: " + user.error);
          return { error: user.error } as LoginVm;
        }
      } catch (apiError: any) {
        if (
          apiError.response != '"User not found"' &&
          apiError.response != `"Password didn't Match"`
        ) {
          console.error(`Error logging in`, apiError);
        }

        var errMsg = apiError.response ?? apiError.toString();
        return { error: errMsg } as LoginVm;
      }
    },
    async setPersonalPassword(
      email: string,
      oldpass: string,
      pass: string,
      portalId: number
    ): Promise<string | null> {
      console.log(`Setting personal password for ${email} `);

      try {
        await userClient.upgradePassword(email, oldpass, pass, portalId);
      } catch (apiError: any) {
        console.error(`Error Setting password`, apiError);
        return apiError.response ?? apiError.toString();
      }

      console.log("Successfully set personal password for " + email);
      return null;
    },
    // async loginToken(
    //   token: string,
    //   portalId: number,
    //   siteCode?: string,
    //   clientName?: string
    // ): Promise<LoginVm> {
    //   console.log(`Logging in user `, token);
    //   // let self = this;

    //   try {
    //     let user = await userClient.login2(token, portalId, clientName);
    //     if (user && !user.error) {
    //       console.log(`Successfully logged in as ${user.person.loginEmail}`, user);

    //       console.log("Loaded User", user.person);
    //       this.user = user.person;
    //       if (siteCode) {
    //         localStorage[siteCode + userStorageKey] = JSON.stringify(user.person);
    //         this.isUserLoadedFromServer = true;
    //         this.isUserLoading = false;
    //       }
    //       await useBldgStore().tryGetBuildingData(useBldgStore().bldgCode, true);
    //       return user;
    //     } else {
    //       console.warn("Error logging in: " + user.error);
    //     }
    //   } catch (apiError: any) {
    //     console.error(`Error logging in`, apiError);
    //     var errMsg = apiError.response ?? apiError.toString();
    //     return { error: errMsg } as LoginVm;
    //   }
    // },
    async createAccount(registeringUser: RegisterRequest): Promise<string | null> {
      console.log("registering user", registeringUser);
      try {
        var token = await userClient.register(registeringUser);

        if (token?.length > 0) {
          this.setAuthToken(token);
          return null; //No error
        } else {
          console.warn("Error registering user");
          return "Error Registering user ";
        }
      } catch (apiError: any) {
        console.error("error while registering new user", apiError.reponse);
        return apiError?.response ?? apiError.message;
      }
    },
    async checkClient(clientSystemName: string): Promise<boolean> {
      try {
        return await userClient.checkClient(clientSystemName);
      } catch (apiError: any) {
        console.error("error checking client", apiError.reponse);
        return false;
      }
    },
    async impersonateUser(
      userName: string,
      portalId?: number,
      clientSystemName?: string,
      regionName?: string,
      siteCode?: string
    ): Promise<LoginVm> {
      //console.log(`Logging in user ${userName}`);
      const impersonateRequest = {
        userName,
        portalId,
        clientSystemName,
        regionName,
      } as ImpersonateRequest;

      try {
        let user = await userClient.impersonate(impersonateRequest);
        if (user && user.person && !user.error && !user.upgradePassword) {
          if (user.person.token) {
            this.setAuthToken(user.person.token);
          }

          this.user = user.person;

          let userString = JSON.stringify(user.person);
          if (siteCode) {
            localStorage[siteCode + userStorageKey] = userString; // @ts-ignore - TODO: Fix this null check
          } else if (clientSystemName && user.person?.allRegistrations?.length > 0) {
            for (var i in user.person.allRegistrations) {
              // @ts-ignore - TODO: Fix this null check
              var portalRegistration = user.person.allRegistrations[i] as LoginRegistrationDTO;
              if (portalRegistration?.siteCode) {
                localStorage[portalRegistration?.siteCode + userStorageKey] = userString;
              }
            }
          }

          this.isUserLoadedFromServer = true;
          this.isUserLoading = false;
          console.log(`Successfully logged in as ${userName}`, user);

          await useBldgStore().tryGetBuildingData(useBldgStore().bldgCode, true);

          return user;
        } else if (user && user.upgradePassword) {
          return user;
        } else {
          console.warn("Error logging in: " + user.error);
          return { error: user.error } as LoginVm;
        }
      } catch (apiError: any) {
        if (
          apiError.response != '"User not found"' &&
          apiError.response != `"Password didn't Match"`
        ) {
          console.error(`Error logging in`, apiError);
        }

        var errMsg = apiError.response ?? apiError.toString();
        return { error: errMsg } as LoginVm;
      }
    },
    async requestEmailVerification(username: string, siteCode: string): Promise<string | null> {
      console.log(`Email verification requested for ${username}.`);
      try {
        await userClient.requestEmailVerification(username, siteCode);
      } catch (apiError: any) {
        console.error("Error requesting verification email", apiError);
        return apiError.response ?? apiError.toString();
      }

      return null; //Successful. Return no error message
    },
    async verifyEmail(username: string, code: string): Promise<string | null> {
      console.log(`Confirm Email for ` + username);
      try {
        await userClient.verifyEmail(username, code);
      } catch (apiError: any) {
        console.error("Error confirming email", apiError);
        return apiError.response ?? apiError.toString();
      }

      return null; //Successful. Return no error message
    },
    async staffForceVerifyEmail(username: string): Promise<string | null> {
      console.log(`Force Confirm Email for ` + username);
      try {
        await userClient.staffForceVerifyEmail(username);
      } catch (apiError: any) {
        console.error("Error force confirming email", apiError);
        return apiError.response ?? apiError.toString();
      }

      return null; //Successful. Return no error message
    },
    hasPermission(permission: PermissionType) {
      return this.user?.permissions?.some((p) => p.permissionType == permission);
    },
    hasAnyPermission(permissions: PermissionType[]) {
      // @ts-ignore - TODO: Fix this null check
      return this.user?.permissions?.some((p) => permissions.includes(p.permissionType));
    },
    loadAuthToken(siteCode: string): string | null {
      var token = getAuthToken(siteCode); // @ts-ignore - TODO: Fix this null check
      userClient.setAuthToken(token);
      return token;
    },
    async needsUpgrade(username: string, portalId: number): Promise<string> {
      try {
        return await userClient.needsUpgrade(username, portalId);
      } catch (apiError: any) {
        // @ts-ignore - TODO: Fix this null check
        return null;
      }
    },
  },
  getters: {
    isLoggedIn(state) {
      // @ts-ignore - TODO: Fix this null check
      return state.user?.loginEmail?.length > 0;
    },
    isEmailVerifiedOrNotRequired(state) {
      if (state.user?.isEmailVerificationRequired) {
        return state.user?.isEmailVerified;
      }
      return true;
    },
    isStaffUser(state) {
      return state.user?.loginEmail?.endsWith("@akpreparedness.com") ?? false;
    },
    authToken(state) {
      return userClient.authToken || state.user?.token;
    },
    permissions(state) {
      return state.user?.permissions?.map((p) => p.permissionType) ?? [];
    },
    isAdmin(state) {
      return state.user?.permissions?.some((p) => p.permissionType != null);
    },
  },
});

function getAuthToken(siteCode: string): string | null {
  // Prefer the token stored for this specific siteCode. The in-memory token is now
  // shared by every API client, so falling back to it first could carry a token
  // belonging to a different portal into this one.
  let token: string | null = null;
  // @ts-ignore - TODO: Fix this null check
  var localUserKey =  Object.keys(localStorage).find(k=>k.toLowerCase() == siteCode + userStorageKey)
  var localUser = JSON.parse(localStorage.getItem(localUserKey!)) as LoginPersonDTO;
  if (localUser) {
    // @ts-ignore - TODO: Fix this null check
    token = localUser.token;
  }

  if (!token) {
    token = userClient.authToken;
  }

  if (!token) {
    // @ts-ignore - TODO: Fix this null check
    token =
      document.cookie
        ?.split("; ")
        ?.find((row) => row.startsWith("Bearer="))
        ?.split("=")[1] || null;
  }

  userClient.authToken = token;
  return token;
}

//Enable Hot Module Replacement
// https://pinia.esm.dev/cookbook/hot-module-replacement.html
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useUserStore as any, (import.meta as any).hot));
}
